import type { Optional } from '@festina-life/core'

import { useTemplateContext } from '../../context/templateContext'
import { useLazyLayerMetaModel } from '../../context/useLazyLayerMetaModel'
import {
  type ConfigurationTemplateFunctionValue,
  isDeclaredConfigurationTemplateFunctionValue,
  isTemplateField,
  type SupportedEditableTemplateFieldValue,
  type TemplateField,
} from '../../types/templateModel'
import {
  generateEmptyDeclaredFunctionValue,
  generateEmptyFieldValue,
} from '../../uiModels/createEmptyFieldHelpers'
import { isInherited } from '../../uiModels/templateOperations/types'
import { InheritedValueCopier } from '../copyInheritedValue/InheritedValueCopier'
import { EditableMarkdownViewer } from '../markdownEditor/EditableMarkdownViewer'
import { documentationFromTemplateField } from './helpers'

type Props = {
  field: TemplateField | ConfigurationTemplateFunctionValue
}

export const TemplateFieldDocumentationEditor = ({ field }: Props) => {
  const { isDraftTemplate, templateVersion } = useTemplateContext()
  const metaModel = useLazyLayerMetaModel(templateVersion?.layer?.layerId)

  const documentation = documentationFromTemplateField(field)

  const handleSaveDocumentation = (updatedDocumentation: Optional<string>) => {
    if (isTemplateField(field)) {
      const fieldValue =
        field.value ??
        generateEmptyFieldValue({
          field,
          meta: metaModel,
        })

      ;(
        field.update as (nextValue: SupportedEditableTemplateFieldValue) => void
      )({
        ...fieldValue,
        documentation: updatedDocumentation
          ? {
              value: updatedDocumentation,
              origin: `${fieldValue.path}.documentation`,
            }
          : undefined,
        origin: fieldValue.path,
      })
    } else if (isDeclaredConfigurationTemplateFunctionValue(field)) {
      // For declared functions to match it's important for the function value to at least contain the function signature.
      const functionValue = field.function?.fplusSource
        ? field
        : generateEmptyDeclaredFunctionValue(field, field.descriptor)
      field.update({
        ...functionValue,
        documentation: updatedDocumentation
          ? {
              value: updatedDocumentation,
              origin: `${functionValue.path}.documentation`,
            }
          : undefined,
        origin: functionValue.path,
      })
    } else {
      field.update({
        ...field,
        documentation: updatedDocumentation
          ? {
              value: updatedDocumentation,
              origin: `${field.path}.documentation`,
            }
          : undefined,
        origin: field.path,
      })
    }
  }

  const documentationPath = `${field.path}.documentation`
  const isInheritedDocumentation =
    !!documentation?.value &&
    isInherited({
      origin: documentation.origin,
      path: documentationPath,
    })

  return (
    <div className="group relative">
      <EditableMarkdownViewer
        value={documentation?.value}
        onSave={
          isDraftTemplate && !isInheritedDocumentation
            ? handleSaveDocumentation
            : undefined
        }
      />
      {isInheritedDocumentation && (
        <InheritedValueCopier
          onCopy={() => handleSaveDocumentation(documentation?.value)}
          fromOrigin={documentation?.origin}
          fieldPath={documentationPath}
        />
      )}
    </div>
  )
}
